import React, { useState, useEffect, useCallback } from 'react';

import { Skeleton } from '@material-ui/lab';
import { updateFeedTalkingPointCommentAction } from 'actions/feed';
import CommentContainer from 'components/Comment/CommentContainer';
import { useMatchedRoute } from 'components/hooks/useMatchedRoute';
import { usePagination } from 'components/hooks/usePagination';
import usePrevious from 'components/hooks/usePrevious';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import { StyledBox, StyledButton, StyledGrid, StyledTypography, StyledTooltip } from 'styles/common';

import { CHECK_IN_COMMENT_PLACEHOLDER } from 'constants/ProjectConstants';
import { ChatBubbleIcon } from 'constants/icons';
import {
  ONE_ON_ONE_EVENTS_OLD,
  sendAddedCommentsToOneononeQuestions,
  sendOneOnOneEvent,
} from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { showErrorSnackbar } from 'services/snackbar';
import { getTalkingPointComments } from 'services/task';

import CreateTalkingPointDiscussion from './CreateTalkingPointDiscussion';

const TalkingPointDiscussion = ({
  oneOnOneInstanceId,
  talkingPointQuestionId,
  commentsUnReadCount,
  totalCommentCount,
  showTalkingPointsPanel = null,
  isSubmitted = false,
  talkingPointQuestionVisibility,
  isRequired,
  isAnswered,
  markRequiredQuestionAnswered,
  isDisabled,
}) => {
  const [comments, setComments] = useState([]);
  const [currentCommentCount, setCurrentCommentCount] = useState(totalCommentCount || 0);

  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { page, hasMore, updatePagination } = usePagination();
  const uuid = talkingPointQuestionId;
  const prevOneOnOneInstanceId = usePrevious(oneOnOneInstanceId);

  const { pageName } = useMatchedRoute();

  const loadComments = () => {
    if (loading) return;
    setLoading(true);
    const limit = 4;
    const offset = oneOnOneInstanceId === prevOneOnOneInstanceId ? page * limit : 0;

    getTalkingPointComments({
      checkInId: oneOnOneInstanceId,
      talkingPointQuestionId,
      limit,
      offset,
    })
      .then((comments) => {
        updatePagination({
          hasMore: comments.length === limit,
          page: oneOnOneInstanceId === prevOneOnOneInstanceId ? page + 1 : 1,
        });
        setComments((state) => [...state, ...comments]);
      })
      .catch(() => {
        dispatch(showErrorSnackbar('Sorry an error occured while fetching the task comments'));
      })
      .finally(() => setLoading(false));
  };

  const addComment = useCallback(
    (comment) => {
      setComments((prev) => [comment, ...prev]);
      // Send Mark Complete or revertComplete
      sendAddedCommentsToOneononeQuestions({
        eventProperties: {
          role: loggedInEmployee.userRole,
        },
      });
      sendOneOnOneEvent({
        eventName: ONE_ON_ONE_EVENTS_OLD.answeredTalkingPoints,
        page: {
          pageName,
          flow: 'Activity',
        },
        secondaryAction: ONE_ON_ONE_EVENTS_OLD.answeredTalkingPoints,
      });
    },
    [setComments]
  );
  useEffect(() => {
    if (oneOnOneInstanceId) {
      loadComments();
    }
    return () => setComments([]);
  }, [setComments, oneOnOneInstanceId]);

  useEffect(() => {
    if (isRequired && !isAnswered && comments.length) markRequiredQuestionAnswered(talkingPointQuestionId);
  }, [isAnswered, markRequiredQuestionAnswered, isRequired, comments.length]);

  const updateLocalAndFeedComment = useCallback(
    ({ commentId, comment }) => {
      setComments((comments) => comments.map((c) => (c.id === commentId ? { ...comment } : c)));
      // on update we get only part of the feed that is post and hence we don't need
      // to normalize it before adding it to the store
      dispatch(
        updateFeedTalkingPointCommentAction({
          commentId,
          comment,
          talkingPointQuestionId,
        })
      );

      // Send Mark Complete or revertComplete
      sendOneOnOneEvent({
        eventName: ONE_ON_ONE_EVENTS_OLD.answeredTalkingPoints,
        page: {
          pageName,
          flow: 'Activity',
        },
        secondaryAction: ONE_ON_ONE_EVENTS_OLD.updatedTalkingPoint,
      });
    },
    [dispatch]
  );

  return (
    <Container>
      {!loading && (
        <StyledGrid container alignItems="flex-start" justify="space-between">
          {isSubmitted ? (
            <StyledButton
              color="primary"
              bottomGutter={1}
              topGutter={1}
              leftGutter={2}
              variant="text"
              onClick={showTalkingPointsPanel || loadComments}
              disabled={loading}
            >
              View all comments
            </StyledButton>
          ) : (
            <CreateTalkingPointDiscussion
              addComment={addComment}
              taskId={oneOnOneInstanceId}
              currentCheckInInstanceId={oneOnOneInstanceId}
              loading={false}
              placeholder="Enter comment"
              talkingPointQuestionId={talkingPointQuestionId}
              onAddComment={() => setCurrentCommentCount((prevCount) => prevCount + 1)}
              talkingPointQuestionVisibility={talkingPointQuestionVisibility}
              isDisabled={isDisabled}
            />
          )}

          <StyledGrid container zeroMinWidth alignItems="center" topGutter={2} style={{ width: 'auto' }}>
            <StyledTooltip title="Show all comments">
              <StyledButton
                style={{ minWidth: 'auto' }}
                padding="0.5rem 0.5rem"
                customColor={
                  commentsUnReadCount > 0 ? theme.palette.background.lightBlue : theme.palette.background.lightGrey3
                }
                fontColor="white"
                onClick={showTalkingPointsPanel}
              >
                <ChatBubbleIcon
                  size="tiny"
                  color={commentsUnReadCount > 0 ? 'primary' : 'textSecondary'}
                  style={{ transform: 'scaleX(-1)' }}
                />
              </StyledButton>
            </StyledTooltip>
            <StyledTypography variant="body2" color="textSecondary" leftGutter={1}>
              {currentCommentCount}
            </StyledTypography>
          </StyledGrid>
        </StyledGrid>
      )}
      {comments &&
        comments.map((comment) => (
          <StyledCommentContainer
            placeholder={CHECK_IN_COMMENT_PLACEHOLDER}
            key={`${comment.id}-taskComment`}
            {...comment}
            updateFeedComment={updateLocalAndFeedComment}
            feedId={oneOnOneInstanceId}
            feedType="TASK"
            displayReply={false}
            styleType="default"
            employeeId={loggedInEmployee.id}
            // renderDisplayCommentView={({ subType, ...props }) =>
            //   subType === Post.SUB_TYPES.automatedComment ? (
            //     <AutomatedComment {...props} />
            //   ) : (
            //     <SidepanelCommentDisplay {...props} />
            //   )
            // }
            talkingPointQuestionId={talkingPointQuestionId}
            visibilityChangeDenyTooltip="Visibility change not allowed for private talking point"
            useTeamIcon
            allowVisibilityEdit={talkingPointQuestionVisibility !== 'PRIVATE'}
            rightAlignCommentTime
            isDisabled={isDisabled}
          />
        ))}
      {loading ? (
        <StyledBox topGutter={1}>
          <Skeleton width="100%" />
          <Skeleton width="100%" />
        </StyledBox>
      ) : (
        <>
          {!isSubmitted && hasMore && (
            <StyledButton
              color="primary"
              bottomGutter={1}
              size="small"
              topGutter={-0.5}
              leftGutter={2}
              variant="text"
              onClick={showTalkingPointsPanel || loadComments}
              disabled={loading}
            >
              View More
            </StyledButton>
          )}
        </>
      )}
    </Container>
  );
};

export default TalkingPointDiscussion;

const Container = styled.div`
  .automatedComment + .automatedComment {
    margin-bottom: -0.5rem;
  }
  position: relative;
  // overflow-y: auto;
  width: 100%;
  ${'' /* padding-right: 1rem; */}
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledCommentContainer = styled(CommentContainer)`
  width: 80%;
`;
